/**
 * Talent-exclusive gear gate — the single source the reducer wall
 * (`equip_weapon`/`equip_system`/`attach_mod`) and the editor-kit affordance both
 * import, mirroring `licenses.ts`'s `licenseRequirement`/`meetsLicenseRequirement`
 * shape.
 *
 * **The item self-identifies — no compendium-build map to author.** In
 * lancer-data 4.0.5 every talent-granted item already carries `talent_id`
 * (and `talent_rank`) on the item itself: `ms_technophile_1`/`2`/`3` (Servant/
 * Student/Enlightenment-Class NHP, Technophile r1/2/3), `ms_walking_armory_1`/
 * `2`/`3` (Ammo Case I/II/III, Walking Armory r1/2/3), `mw_prototype_1`/`2`/`3`
 * (Prototype Weapon I/II/III, Engineer r1/2/3), and `mw_fuel_rod_gun` (Fuel Rod
 * Gun, Nuclear Cavalier r3). `WeaponSchema`/`SystemSchema` are `.passthrough()`
 * (`packages/compendium/src/schemas.ts`), so these fields ride through
 * verbatim into the built compendium at runtime — this module just reads them.
 *
 * **All 10 items are GMS `license_level: 0`** — the existing license gate
 * (`licenses.ts`) is structurally blind to them (GMS always passes via the
 * seeded rank), which is precisely the gap this gate closes.
 *
 * **Equip-time-only, no loss-time sweep — mirroring the license precedent
 * exactly** (`applySetLicenseRank` doesn't sweep now-illegal gear on a rank
 * drop, and neither does `applySetTalentRank`). Dropping a talent rank leaves
 * already-equipped talent gear equipped. Don't add a revalidation sweep here;
 * if one is ever wanted, it should be a single holistic pass over *all*
 * equip-time gates (license + talent), not a talent-only special case.
 */
import type { PilotData } from "./types";

/**
 * The minimal shape of a compendium item the gate reads (all fields
 * optional/passthrough, mirroring `licenses.ts`'s `LicensedItem`). An item with
 * no `talent_id` is ungated (freely equippable, subject to the other gates).
 */
export type TalentGatedItem = {
  talent_id?: string;
  talent_rank?: number;
};

/**
 * The `(talent id, required rank)` an item demands, or `null` when the item
 * carries no `talent_id` (always equippable w.r.t. this gate). A `talent_id`
 * with no `talent_rank` defaults to rank 1 (every real item in the data
 * carries both, but the reader stays defensive).
 */
export function talentRequirement(
  item: TalentGatedItem,
): { id: string; rank: number } | null {
  if (!item.talent_id) return null;
  return { id: item.talent_id, rank: item.talent_rank ?? 1 };
}

/**
 * Does the pilot meet the talent requirement to equip this compendium item?
 * `true` when the item carries no `talent_id` (ungated), or when the pilot's
 * owned rank in the item's talent ≥ the item's `talent_rank`. The single
 * predicate the equip-gate reducer rules and the kit affordance both consult, so
 * the wall and the affordance can't drift.
 */
export function meetsTalentRequirement(pilot: PilotData, item: TalentGatedItem): boolean {
  const req = talentRequirement(item);
  if (!req) return true;
  return (pilot.talents[req.id] ?? 0) >= req.rank;
}
